import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaStar, FaChevronLeft, FaChevronRight, FaArrowRight, FaHeart } from 'react-icons/fa';
import { useToast } from '../contexts/ToastContext';
import './ProductSlider.css';
import config from '../services/config.json';

const ProductSlider = ({ products, title, subtitle, arrowLink, allOnMobile = false }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [wishlistItems, setWishlistItems] = useState(new Set());
  const [isNarrowViewport, setIsNarrowViewport] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  const [rowScrollClamped, setRowScrollClamped] = useState({ atStart: true, atEnd: false });
  const productsRowRef = useRef(null);
  const navigate = useNavigate();
  const { showToast } = useToast();

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const sync = () => setIsNarrowViewport(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const slidesPerView =
    allOnMobile && isNarrowViewport && products.length > 0
      ? products.length
      : 4;
  const totalSlides = Math.ceil(products.length / slidesPerView) || 0;

  /** Narrow + single “page” of products: arrows scroll the row, not the slide index. */
  const useRowScrollNav =
    isNarrowViewport && totalSlides === 1 && products.length > 1;

  // Load wishlist items from localStorage on component mount
  useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      const user = JSON.parse(userData);
      const customerId = user.CustomerId || user.Id;
      if (customerId) {
        // Use customer-specific localStorage keys
        const savedWishlist = localStorage.getItem(`wishlistItems_${customerId}`);
        if (savedWishlist) {
          setWishlistItems(new Set(JSON.parse(savedWishlist)));
        }
      }
    }
  }, []);

  const syncRowScrollState = useCallback(() => {
    const el = productsRowRef.current;
    if (!el) return;
    const { scrollLeft, clientWidth, scrollWidth } = el;
    const atStart = scrollLeft <= 1;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;
    setRowScrollClamped((p) => (p.atStart === atStart && p.atEnd === atEnd ? p : { atStart, atEnd }));
  }, []);

  useEffect(() => {
    if (!useRowScrollNav) return;
    const el = productsRowRef.current;
    if (!el) return;
    syncRowScrollState();
    el.addEventListener('scroll', syncRowScrollState, { passive: true });
    window.addEventListener('resize', syncRowScrollState);
    return () => {
      el.removeEventListener('scroll', syncRowScrollState);
      window.removeEventListener('resize', syncRowScrollState);
    };
  }, [useRowScrollNav, products.length, currentSlide, syncRowScrollState]);

  const nextSlide = () => {
    if (totalSlides < 1) return;
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    if (totalSlides < 1) return;
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const scrollRowByOne = (dir) => {
    const el = productsRowRef.current;
    if (!el) return;
    const delta = el.clientWidth;
    el.scrollBy({ left: dir === 'next' ? delta : -delta, behavior: 'smooth' });
  };

  const handleControlPrev = () => {
    if (useRowScrollNav) {
      scrollRowByOne('prev');
    } else {
      prevSlide();
    }
  };

  const handleControlNext = () => {
    if (useRowScrollNav) {
      scrollRowByOne('next');
    } else {
      nextSlide();
    }
  };

  const toggleWishlist = async (productId, productData, e) => {
    e.stopPropagation();
    
    const userData = localStorage.getItem('userData');
    if (!userData) {
      showToast('Please login to continue', 'info', { actionLabel: 'Login', actionTo: '/login' });
      return;
    }

    try {
      const user = JSON.parse(userData);
      const customerId = user.CustomerId || user.Id;

      // Manage localStorage wishlistProducts (full product details) - customer-specific
      const customerSpecificKey = `wishlistProducts_${customerId}`;
      const stored = localStorage.getItem(customerSpecificKey);
      const parsed = stored ? JSON.parse(stored) : [];
      const wishlistProducts = Array.isArray(parsed) ? parsed : [];

      const existingIndex = wishlistProducts.findIndex(p => String(p.Id) === String(productId));
      let updatedSet;

      if (existingIndex !== -1) {
        // Remove from wishlist
        wishlistProducts.splice(existingIndex, 1);
        const newWishlist = new Set(wishlistItems);
        newWishlist.delete(productId);
        updatedSet = newWishlist;
        setWishlistItems(newWishlist);
      } else {
        // Add to wishlist
        const item = {
          Id: productId,
          Name: productData.Name,
          Price: productData.Price,
          OfferPrice: productData.OfferPrice,
          Image: productData.Image1,
          Category: productData.Code,
          Rating: productData.AvgRating
        };
        wishlistProducts.push(item);
        const newWishlist = new Set(wishlistItems);
        newWishlist.add(productId);
        updatedSet = newWishlist;
        setWishlistItems(new Set(newWishlist));
      }

      localStorage.setItem(customerSpecificKey, JSON.stringify(wishlistProducts));
      localStorage.setItem(`wishlistItems_${customerId}`, JSON.stringify([...updatedSet]));
      
      // Refresh wishlist count in header
      if (window.refreshWishlistCount) {
        window.refreshWishlistCount();
      }
      
      // Navigate to product detail page instead of wishlist page
      const encodedName = encodeURIComponent(productData.Name);
      navigate(`/product/${productId}/${encodedName}`);
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      showToast('Failed to update wishlist', 'error');
    }
  };

  const normalizeCategory = (rawCategory) => {
    if (!rawCategory) return 'General';
    if (typeof rawCategory === 'string') return rawCategory;
    if (typeof rawCategory === 'object') {
      return rawCategory.Name || rawCategory.Code || rawCategory.Title || 'General';
    }
    return 'General';
  };

  const getProductData = (product) => ({
    id: product.Id || product.id,
    Name: product.Name || product.name,
    Price: product.Price || product.price || 0,
    OfferPrice: product.OfferPrice || product.offerPrice || null,
    AvgRating: product.AvgRating || product.avgRating || product.rating || 0,
    Image1: product.Image1 || product.image1 || product.image || '',
    Code: normalizeCategory(product.Code || product.Category || product.category)
  });

  const handleNavigate = (p) => {
    const pd = getProductData(p);
    if (!pd.id || !pd.Name) return;
    navigate(`/product/${encodeURIComponent(pd.id)}/${encodeURIComponent(pd.Name)}`);
  };

  const titleText = typeof title === 'string' ? title : (title && typeof title === 'object' && (title.Name ?? title.name)) ? String(title.Name ?? title.name) : 'Products';

  const showNavControls = products.length > 1 && (isNarrowViewport || totalSlides > 1);

  const navPrevDisabled = useRowScrollNav ? rowScrollClamped.atStart : currentSlide === 0;

  const navNextDisabled = useRowScrollNav
    ? rowScrollClamped.atEnd
    : currentSlide === totalSlides - 1;

  return (
    <div className="product-slider-container">
      <div className="slider-header">
        <div className="slider-title-section">
          <h2 className="slider-title">
            {titleText}
            {arrowLink ? (
              <Link to={arrowLink}>
                <FaArrowRight className="title-arrow clickable" />
              </Link>
            ) : (
              <>
                {titleText === "Latest Products" && (
                  <Link to="/products?id=0&type=latest&name=all">
                    <FaArrowRight className="title-arrow clickable" />
                  </Link>
                )}
                {titleText === "Top Picks For You" && (
                  <Link to="/products?id=0&type=top%20picks&name=all">
                    <FaArrowRight className="title-arrow clickable" />
                  </Link>
                )}
                {titleText === "Top Selling Products" && (
                  <Link to="/products?id=0&type=top%20selling&name=all">
                    <FaArrowRight className="title-arrow clickable" />
                  </Link>
                )}
                {titleText !== "Latest Products" && titleText !== "Top Picks For You" && titleText !== "Top Selling Products" && (
                  <FaArrowRight className="title-arrow" />
                )}
              </>
            )}
          </h2>
          {subtitle && <p className="slider-subtitle">{subtitle}</p>}
        </div>
        
        {showNavControls && (
        <div className="slider-controls">
          <button 
            className="slider-btn prev-btn" 
            type="button"
            onClick={handleControlPrev}
            disabled={navPrevDisabled}
            aria-label="Previous"
          >
            <FaChevronLeft />
          </button>
          
          <button 
            className="slider-btn next-btn" 
            type="button"
            onClick={handleControlNext}
            disabled={navNextDisabled}
            aria-label="Next"
          >
            <FaChevronRight />
          </button>
        </div>
        )}
      </div>

      <div className="product-slider">
        {/* One page in the DOM per current slide (4 on desktop; all items in one row when allOnMobile + narrow). */}
        <div className="slider-track">
          <div key={currentSlide} className="slider-slide">
            <div className="products-row" ref={productsRowRef}>
              {products
                .slice(currentSlide * slidesPerView, (currentSlide + 1) * slidesPerView)
                .map((product) => {
                    const productData = getProductData(product);
                    const isInWishlist = wishlistItems.has(productData.id);
                    
                    return (
                      <div key={productData.id} className="product-card" onClick={() => handleNavigate(product)} role="button">
                        <div className="product-image">
                          <img
                            src={productData.Image1 ? (productData.Image1.startsWith('http') ? productData.Image1 : (config.apiUrl + productData.Image1)) : 'https://via.placeholder.com/300x300/333/fff?text=Product'}
                            alt={productData.Name}
                            decoding="async"
                          />
                          <div className="product-actions">
                          <button
                              className={`wishlist-btn ${isInWishlist ? 'in-wishlist' : ''}`}
                              onClick={(e) => toggleWishlist(productData.id, productData, e)}
                              title={isInWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
                          >
                              <FaHeart />
                          </button>
                          </div>
                        </div>
                        <div className="product-info">
                          <span className="product-category">{productData.Code}</span>
                          <h3 className="product-name">{productData.Name}</h3>
                          {productData.AvgRating > 0 && (
                            <div className="product-rating">
                              <div className="stars">
                                {[...Array(5)].map((_, i) => (
                                  <FaStar
                                    key={i}
                                    className={i < Math.floor(productData.AvgRating) ? 'star filled' : 'star'}
                                  />
                                ))}
                              </div>
                              <span className="rating-text">({productData.AvgRating})</span>
                            </div>
                          )}
                          <div className="product-price">
                            {productData.OfferPrice && productData.OfferPrice < productData.Price ? (
                              <>
                                <span className="price original-price">AED {productData.Price}</span>
                                <span className="price offer-price">AED {productData.OfferPrice}</span>
                              </>
                            ) : (
                              <span className="price">AED {productData.Price}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductSlider;
